import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel } from "@/components/Shell";

const inp = "w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500";

export const UsersPanel = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "accountant" });
  const load = () => api.get("/auth/users").then(({ data }) => setUsers(data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/users", form); toast.success("User added"); setForm({ name: "", email: "", password: "", role: "accountant" }); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const setRole = async (u, role) => {
    try { await api.patch(`/auth/users/${u.id}`, { role }); toast.success("Role updated"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const resetPw = async (u) => {
    const password = window.prompt(`New password for ${u.email} (min 8 chars)`);
    if (!password) return;
    try { await api.patch(`/auth/users/${u.id}`, { password }); toast.success("Password reset"); }
    catch (err) { toast.error(apiError(err)); }
  };
  const remove = async (u) => {
    try { await api.delete(`/auth/users/${u.id}`); toast.success("User removed"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Panel title={`Team · ${users.length} users`} className="lg:col-span-3">
      <div className="p-5">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="py-2">Name</th><th className="py-2">Email</th><th className="py-2">Role</th><th className="py-2 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-slate-100 last:border-0" data-testid="user-row">
                <td className="py-2 font-medium text-slate-900">{u.name}{u.id === user.id && <span className="ml-2 text-xs text-slate-400">(you)</span>}</td>
                <td className="py-2 text-slate-600">{u.email}</td>
                <td className="py-2">
                  <select data-testid="user-role-select" value={u.role} disabled={u.id === user.id} onChange={(e) => setRole(u, e.target.value)}
                    className="rounded border border-slate-300 bg-white px-2 py-1 text-xs capitalize disabled:opacity-60">
                    <option value="admin">Admin</option><option value="accountant">Accountant</option><option value="viewer">Viewer</option>
                  </select>
                </td>
                <td className="py-2 text-right">
                  <div className="flex justify-end gap-3">
                    <button data-testid="reset-user-password-button" onClick={() => resetPw(u)} className="text-slate-400 hover:text-slate-900" aria-label="Reset password"><KeyRound size={14} /></button>
                    {u.id !== user.id && <button data-testid="delete-user-button" onClick={() => remove(u)} className="text-slate-400 hover:text-rose-600" aria-label="Remove user"><Trash2 size={14} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <form onSubmit={add} className="mt-4 grid gap-2 sm:grid-cols-5" data-testid="add-user-form">
          <input data-testid="new-user-name-input" required placeholder="Full name" className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input data-testid="new-user-email-input" required type="email" placeholder="Email" className={inp} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input data-testid="new-user-password-input" required minLength={8} placeholder="Password (min 8)" className={inp} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          <select data-testid="new-user-role-select" className={inp} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            <option value="admin">Admin</option><option value="accountant">Accountant</option><option value="viewer">Viewer</option>
          </select>
          <button data-testid="add-user-button" className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">Add user</button>
        </form>
      </div>
    </Panel>
  );
};
